import React from 'react';

import { c } from 'ttag';

import { Badge, Button } from '@freelancelabs/components';

import { ContactsByEmailQuery } from '../../../graphql/__generated__/types';
import { EMPTY, formatDateTime } from '../display';
import { contactFullName, contactStatusReading } from './contactLookup';

export type MatchedContact = NonNullable<ContactsByEmailQuery['contactsByEmail']>['contacts'][number];

interface Props {
    contact: MatchedContact;
    /** Whether this is the contact the draft carries. */
    selected: boolean;
    /** True as soon as the address resolved to more than one contact. */
    amongSeveral: boolean;
    onPick: () => void;
    onUnpick: () => void;
}

/**
 * One contact the address resolved to: **the identity as the source writes it**,
 * and the button that adopts it.
 *
 * The card is the mockup's « Contact identifié » block, with its fields in the same
 * order. Two things it does that the mockup does not, both required by the decision
 * behind PCG-84:
 *
 * - it is a **candidate**, not a result. The mockup searches an External ID, which
 *   is unique, so it can display the one contact it found; an address is not unique,
 *   so several of these can sit side by side and the operator picks. Hence the button
 *   rather than a bare display;
 * - it carries the **onboarding status** and what that status means for the rest of
 *   the journey, read off the API's scale rather than re-derived from the fields.
 *
 * Attention, A contact that cannot be onboarded stays on screen, with its reason, and its
 * button is **disabled rather than removed**: the same rule as a refused role or a
 * closed account. « Vous ne pouvez pas onboarder ce contact » is not « cette adresse
 * est inconnue », and dropping the card would say the second.
 */
const ContactMatchCard = ({ contact, selected, amongSeveral, onPick, onUnpick }: Props) => {
    const reading = contactStatusReading(contact.onboardingStatus);
    const name = contactFullName(contact);

    /**
     * The source's own accounts for this contact, **truncated to the operator's
     * perimeter** by the API. Empty is a real answer: it is the `NO_ACCOUNT` contact,
     * findable through its root precisely so its refusal can be shown, so the list is
     * never rendered as a blank.
     *
     * Attention, these are where the contact sits **at the source**, not what the invitation
     * will grant: step 2 picks that, and may pick fewer or others.
     */
    const accounts = contact.accounts;

    return (
        <li
            className={
                selected
                    ? 'rounded-lg border border-admin-accent-border bg-admin-accent-soft p-4'
                    : 'rounded-lg border border-border-norm p-4'
            }
        >
            <div className="flex flex-wrap items-start gap-3">
                <div>
                    <h3 className="text-sm font-semibold text-text-900">
                        {name ?? c('Onboarding contact').t`Contact without a name`}
                    </h3>
                    {/* The address the SOURCE writes, not the one that was typed: the
                        comparison is case-insensitive, so the two can differ. */}
                    <p className="font-mono text-xs text-text-600">{contact.email ?? EMPTY}</p>
                </div>
                <span className="ml-auto flex flex-wrap items-center gap-2">
                    <Badge category={reading.tone} size="small">
                        {reading.tag}
                    </Badge>
                    {selected && (
                        <Badge category="success" size="small">
                            {c('Onboarding contact').t`Picked`}
                        </Badge>
                    )}
                </span>
            </div>

            <dl className="mt-3 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
                <div>
                    <dt className="text-text-600">{c('Onboarding contact field').t`Function`}</dt>
                    {/* Verbatim. « DIRECTRICE DES REGIONS » sits next to « Ingénieur
                        d'affaires »: these are real job titles, and normalising them on
                        screen would invent titles nobody carries. */}
                    <dd className="text-text-900">{contact.title ?? EMPTY}</dd>
                </div>
                <div>
                    <dt className="text-text-600">{c('Onboarding contact field').t`Phone`}</dt>
                    <dd className="font-mono text-text-900">{contact.phone ?? EMPTY}</dd>
                </div>
                <div>
                    <dt className="text-text-600">{c('Onboarding contact field').t`Source reference`}</dt>
                    <dd className="font-mono text-text-900">{contact.businessReference ?? EMPTY}</dd>
                </div>
                <div>
                    {/* A replicated record with no refresh date reads as an up-to-date
                        one. It is a rule of the ingestion, not a display detail. */}
                    <dt className="text-text-600">{c('Onboarding contact field').t`Source last updated`}</dt>
                    <dd className="text-text-900">{formatDateTime(contact.sourceLastModifiedAt)}</dd>
                </div>
                <div className="sm:col-span-2">
                    <dt className="text-text-600">{c('Onboarding contact field').t`Companies at the source`}</dt>
                    <dd className="text-text-900">
                        {accounts.length === 0 ? (
                            <span className="text-text-600">
                                {c('Onboarding contact')
                                    .t`None within your portfolio — which is why this contact cannot be onboarded.`}
                            </span>
                        ) : (
                            <ul className="flex flex-col gap-0.5">
                                {accounts.map((account) => (
                                    <li key={account.id} className="flex flex-wrap items-center gap-2">
                                        <span>{account.name}</span>
                                        {/* Display only: the primary account drives no
                                            right and is not step 2's default. */}
                                        {account.isPrimary && (
                                            <Badge category="default" size="small">
                                                {c('Onboarding contact').t`Main company`}
                                            </Badge>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </dd>
                </div>
            </dl>

            {/* What the status means for the rest of the journey: always said, not
                only when it refuses: « déjà onboardé » changes what the journey does
                without stopping it, and an operator who is not told reads the next
                steps as creating a second account. */}
            {reading.meaning && (
                <p
                    className={reading.selectable ? 'mt-3 text-sm text-text-600' : 'mt-3 text-sm text-danger-600'}
                    role={reading.selectable ? 'status' : 'alert'}
                >
                    {reading.meaning}
                </p>
            )}

            <div className="mt-3 flex flex-wrap items-center gap-3">
                {selected ? (
                    <Button size="small" category="weak" onClick={onUnpick}>
                        {c('Onboarding action').t`This is not the right contact`}
                    </Button>
                ) : (
                    <Button size="small" category="default" disabled={!reading.selectable} onClick={onPick}>
                        {/* Two labels, because the gesture is not the same: with one
                            candidate it confirms an identity, with several it settles
                            which of them. A single « Choisir » would read as a formality
                            on the screen where it is actually the decision. */}
                        {amongSeveral
                            ? c('Onboarding action').t`Onboard this one`
                            : c('Onboarding action').t`This is the right contact`}
                    </Button>
                )}
                {/* Why the button refuses, next to the button. The long reason is above;
                    this is the short qualifier, so the disabled control is never mute. */}
                {!reading.selectable && !selected && <span className="text-sm text-text-600">{reading.tag}</span>}
            </div>
        </li>
    );
};

export default ContactMatchCard;
